import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Shield, MoreVertical, Eye } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { formatDateDisplay } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { formatErrorMessage } from '../utils/formatError';

const Users = ({ currentUser }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useDeviceDetection();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [editPermissionsDialogOpen, setEditPermissionsDialogOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState({});
  const [editingUserName, setEditingUserName] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: '',
    customRole: '',
    permissions: {}
  });

  // Permission structure with sub-pages
  const defaultPermissions = {
    dashboard: { 
      view: true, create: false, edit: false, delete: false,
      subPages: {
        criticalAlerts: { view: false, edit: false }
      }
    },
    motorcycles: { 
      view: true, create: false, edit: false, delete: false,
      subPages: {
        history: { view: false },
        handover: { view: false, create: false, edit: false, delete: false },
        takeover: { view: false, create: false, edit: false, delete: false },
        documents: { view: false, create: false, delete: false }
      }
    },
    batteries: { 
      view: true, create: false, edit: false, delete: false,
      subPages: {
        import: { view: false, create: false }
      }
    },
    riders: { 
      view: true, create: false, edit: false, delete: false,
      subPages: {
        documents: { view: false, create: false, delete: false }
      }
    },
    lessees: { 
      view: true, create: false, edit: false, delete: false,
      subPages: {
        documents: { view: false, edit: false },
        downloadDocuments: { view: false }
      }
    },
    maintenance: { 
      view: true, create: false, edit: false, delete: false,
      subPages: {
        repairItems: { view: false, create: false, edit: false, delete: false },
        technicians: { view: false, create: false, delete: false }
      }
    },
    fines: { view: true, create: false, edit: false, delete: false },
    inbox: { view: true, create: false, edit: false, delete: false },
    salik: { 
      view: true, create: false, edit: false, delete: false,
      subPages: {
        import: { view: false, create: false },
        bulkUpdate: { view: false, edit: false }
      }
    },
    talent: { 
      view: true, create: false, edit: false, delete: false,
      subPages: {
        staff: { view: false, create: false, edit: false },
        payroll: { view: false, create: false, edit: false, delete: false, approve: false },
        loanAccounts: { view: false, create: false, edit: false, delete: false },
        contractLoanEntries: { view: false, create: false, edit: false, delete: false },
        emiSchedule: { view: false, create: false, edit: false, delete: false },
        salaryAdvance: { view: false, create: false, edit: false, delete: false }
      }
    },
    tap: { view: true, create: false, edit: false, delete: false },
    hiring: { 
      view: true, create: false, edit: false, delete: false,
      viewOwnOnly: false,  // When true, user can only see candidates they enrolled
      subPages: {
        uploadedDocuments: { upload: false, view: false, download: false, replace: false },
        generatedDocuments: { upload: false, view: false, download: false, replace: false },
        visaDocuments: { upload: false, view: false, download: false, replace: false },
        travelDocuments: { upload: false, view: false, download: false, replace: false },
        advancePayments: { view: false, create: false, edit: false },
        loanContract: { view: false, edit: false },
        changeLessee: { view: false, edit: false },
        approvals: { view: false, edit: false }
      }
    },
    spares: { view: true, create: false, edit: false, delete: false },
    batterySwaps: { 
      view: true, create: false, edit: false, delete: false,
      subPages: {
        swapLocations: { view: false, create: false, edit: false, delete: false },
        alerts: { view: false, edit: false }
      }
    },
    insurance: { view: true, create: false, edit: false, delete: false },
    invoices: {
      view: true, create: false, edit: false, delete: false,
      subPages: {
        creditCards: { view: false, create: false, edit: false, delete: false },
        pettyCashHistory: { edit: false, delete: false }
      }
    },
    inventory: {
      view: true, create: false, edit: false, delete: false,
      subPages: {
        simCards: { view: false, create: false, edit: false, delete: false },
        simStatusLog: { view: false },
        digitalBoxes: { view: false, create: false, edit: false, delete: false },
        ppe: { view: false, create: false, edit: false, delete: false }
      }
    },
    reports: { view: true, create: false, edit: false, delete: false },
    analysis: { view: false, create: false, edit: false, delete: false },
    search: { view: true, create: false, edit: false, delete: false },
    approvals: { 
      view: false, create: false, edit: false, delete: false,
      subPages: {
        handovers: { view: false, edit: false },
        takeovers: { view: false, edit: false },
        accidents: { view: false, edit: false },
        riders: { view: false, edit: false },
        lessees: { view: false, edit: false },
        lesseeDocuments: { view: false, edit: false },
        downloadLesseeDocuments: { view: false }
      }
    },
    users: { view: false, create: false, edit: false, delete: false },
    adminTools: {
      view: false, create: false, edit: false, delete: false,
      subPages: {
        pmsAdmin: { view: false, edit: false },
        payrollReconcile: { view: false, edit: false },
        dataRepair: { edit: false },
        dataAudits: { view: false },
        integrations: { view: false, edit: false },
        whatsapp: { view: false, edit: false },
        settings: { view: false, edit: false }
      }
    }
  };

  // Sub-page display names
  const subPageNames = {
    criticalAlerts: 'Critical Alerts',
    history: 'Vehicle History',
    handover: 'Handover Management',
    takeover: 'Takeover Management',
    documents: 'Documents',
    uploadedDocuments: 'Uploaded Documents',
    generatedDocuments: 'Generated Documents',
    visaDocuments: 'Visa Documents',
    travelDocuments: 'Travel Documents',
    import: 'Import Data',
    repairItems: 'Repair Items',
    technicians: 'Technicians',
    bulkUpdate: 'Bulk Update',
    staff: 'Staff Management',
    payroll: 'Payroll',
    loanAccounts: 'Loan Accounts',
    contractLoanEntries: 'Contract Loan Entries',
    emiSchedule: 'EMI Schedule',
    salaryAdvance: 'Salary Advance',
    swapLocations: 'Swap Locations',
    alerts: 'Swap Alerts',
    handovers: 'Handover Approvals',
    takeovers: 'Takeover Approvals',
    accidents: 'Accident Approvals',
    riders: 'Rider Approvals',
    lessees: 'Lessee Approvals',
    lesseeDocuments: 'Lessee Documents',
    downloadLesseeDocuments: 'Download Lessee Documents',
    documents: 'Documents',
    downloadDocuments: 'Download Documents',
    advancePayments: 'Advance Payments',
    loanContract: 'Loan Contract',
    changeLessee: 'Change Lessee',
    approvals: 'Candidate Approvals',
    simCards: 'SIM Cards',
    simStatusLog: 'SIM Status Log',
    digitalBoxes: 'Digital Boxes',
    ppe: 'PPE',
    creditCards: 'Credit Cards',
    pettyCashHistory: 'Petty Cash Settlement History',
    pmsAdmin: 'PMS Admin Tab',
    payrollReconcile: 'Payroll Reconciliation',
    dataRepair: 'Data Repair & Cleanup Tools',
    dataAudits: 'Data Audits & Diagnostics',
    integrations: 'Integrations & Email Settings',
    whatsapp: 'WhatsApp Console',
    settings: 'Admin Fees & System Settings'
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Fetch users error:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Determine the actual role to use
      const actualRole = formData.role === 'Custom' ? formData.customRole : formData.role;
      
      if (!actualRole || actualRole.trim() === '') {
        toast.error('Please select or enter a role');
        return;
      }
      
      const userData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: actualRole.trim(),
        permissions: Object.keys(formData.permissions).length > 0 ? formData.permissions : defaultPermissions
      };
      await axios.post(`${API}/auth/register`, userData, {
        headers: getAuthHeader()
      });
      toast.success('User added successfully');
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to add user'));
    }
  };

  const handlePermissionToggle = (module, action, subPage = null) => {
    setFormData(prev => {
      const currentModulePerms = prev.permissions[module] || defaultPermissions[module];
      
      if (subPage) {
        // Handle sub-page permission toggle
        const currentSubPages = currentModulePerms.subPages || defaultPermissions[module]?.subPages || {};
        const currentSubPagePerms = currentSubPages[subPage] || defaultPermissions[module]?.subPages?.[subPage] || {};
        return {
          ...prev,
          permissions: {
            ...prev.permissions,
            [module]: {
              ...currentModulePerms,
              subPages: {
                ...currentSubPages,
                [subPage]: {
                  ...currentSubPagePerms,
                  [action]: !currentSubPagePerms[action]
                }
              }
            }
          }
        };
      }
      
      // Handle main module permission toggle
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [module]: {
            ...currentModulePerms,
            [action]: !(currentModulePerms[action] ?? defaultPermissions[module][action])
          }
        }
      };
    });
  };

  const setAllPermissions = (module, value, subPage = null) => {
    setFormData(prev => {
      const currentModulePerms = prev.permissions[module] || defaultPermissions[module];
      
      if (subPage) {
        // Set all permissions for a sub-page
        const currentSubPages = currentModulePerms.subPages || {};
        const subPageDef = defaultPermissions[module]?.subPages?.[subPage] || {};
        const newSubPagePerms = {};
        Object.keys(subPageDef).forEach(action => {
          newSubPagePerms[action] = value;
        });
        return {
          ...prev,
          permissions: {
            ...prev.permissions,
            [module]: {
              ...currentModulePerms,
              subPages: {
                ...currentSubPages,
                [subPage]: newSubPagePerms
              }
            }
          }
        };
      }
      
      // Set all permissions for main module (including sub-pages)
      const newModulePerms = {
        view: value,
        create: value,
        edit: value,
        delete: value
      };
      
      // Also set all sub-pages
      if (defaultPermissions[module]?.subPages) {
        newModulePerms.subPages = {};
        Object.keys(defaultPermissions[module].subPages).forEach(sp => {
          newModulePerms.subPages[sp] = {};
          Object.keys(defaultPermissions[module].subPages[sp]).forEach(action => {
            newModulePerms.subPages[sp][action] = value;
          });
        });
      }
      
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [module]: newModulePerms
        }
      };
    });
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: '',
      customRole: '',
      permissions: {}
    });
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) {
      toast.error('Cannot delete your own account');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API}/users/${userId}`, {
          headers: getAuthHeader()
        });
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        console.error('Delete user error:', error);
        toast.error(formatErrorMessage(error, 'Failed to delete user'));
      }
    }
  };

  // Edit permissions handlers
  const handleOpenEditPermissions = (user) => {
    setEditingUserId(user.id);
    setEditingUserName(user.name);
    
    // Initialize with user's current permissions, properly merging with defaults
    // This ensures all modules and sub-pages have explicit values
    const userPerms = user.permissions || {};
    const mergedPermissions = {};
    
    Object.keys(defaultPermissions).forEach(module => {
      const userModulePerms = userPerms[module] || {};
      const defaultModulePerms = defaultPermissions[module];
      
      mergedPermissions[module] = {
        view: userModulePerms.view ?? defaultModulePerms.view,
        create: userModulePerms.create ?? defaultModulePerms.create,
        edit: userModulePerms.edit ?? defaultModulePerms.edit,
        delete: userModulePerms.delete ?? defaultModulePerms.delete,
      };

      // Preserve module-level special flags from the saved user record
      // (e.g. hiring.viewOwnOnly). Without this, the checkbox would render
      // unchecked even when the flag is actually persisted in the DB.
      Object.keys(userModulePerms).forEach(key => {
        if (!['view', 'create', 'edit', 'delete', 'subPages'].includes(key)) {
          mergedPermissions[module][key] = userModulePerms[key];
        }
      });
      
      // Merge sub-pages if they exist in defaults
      if (defaultModulePerms.subPages) {
        mergedPermissions[module].subPages = {};
        Object.keys(defaultModulePerms.subPages).forEach(subPage => {
          const userSubPagePerms = userModulePerms.subPages?.[subPage] || {};
          const defaultSubPagePerms = defaultModulePerms.subPages[subPage];
          
          mergedPermissions[module].subPages[subPage] = {};
          Object.keys(defaultSubPagePerms).forEach(action => {
            // Use user's value if explicitly set, otherwise use default
            mergedPermissions[module].subPages[subPage][action] = 
              userSubPagePerms[action] !== undefined ? userSubPagePerms[action] : defaultSubPagePerms[action];
          });
        });
      }
    });
    
    setEditingPermissions(mergedPermissions);
    setEditPermissionsDialogOpen(true);
  };

  const handleEditPermissionToggle = (module, action, subPage = null) => {
    setEditingPermissions(prev => {
      const currentModulePerms = prev[module] || defaultPermissions[module];
      
      if (subPage) {
        const currentSubPages = currentModulePerms.subPages || defaultPermissions[module]?.subPages || {};
        const currentSubPagePerms = currentSubPages[subPage] || defaultPermissions[module]?.subPages?.[subPage] || {};
        return {
          ...prev,
          [module]: {
            ...currentModulePerms,
            subPages: {
              ...currentSubPages,
              [subPage]: {
                ...currentSubPagePerms,
                [action]: !currentSubPagePerms[action]
              }
            }
          }
        };
      }
      
      return {
        ...prev,
        [module]: {
          ...currentModulePerms,
          [action]: !(currentModulePerms[action] ?? defaultPermissions[module][action])
        }
      };
    });
  };

  const setAllEditPermissions = (module, value, subPage = null) => {
    setEditingPermissions(prev => {
      const currentModulePerms = prev[module] || defaultPermissions[module];
      
      if (subPage) {
        const currentSubPages = currentModulePerms.subPages || {};
        const subPageDef = defaultPermissions[module]?.subPages?.[subPage] || {};
        const newSubPagePerms = {};
        Object.keys(subPageDef).forEach(action => {
          newSubPagePerms[action] = value;
        });
        return {
          ...prev,
          [module]: {
            ...currentModulePerms,
            subPages: {
              ...currentSubPages,
              [subPage]: newSubPagePerms
            }
          }
        };
      }
      
      const newModulePerms = {
        view: value,
        create: value,
        edit: value,
        delete: value
      };
      
      if (defaultPermissions[module]?.subPages) {
        newModulePerms.subPages = {};
        Object.keys(defaultPermissions[module].subPages).forEach(sp => {
          newModulePerms.subPages[sp] = {};
          Object.keys(defaultPermissions[module].subPages[sp]).forEach(action => {
            newModulePerms.subPages[sp][action] = value;
          });
        });
      }
      
      return {
        ...prev,
        [module]: newModulePerms
      };
    });
  };

  const handleSavePermissions = async () => {
    try {
      // Ensure we have a complete permissions structure before saving
      const completePermissions = {};
      Object.keys(defaultPermissions).forEach(module => {
        const editModule = editingPermissions[module] || {};
        completePermissions[module] = {
          view: editModule.view ?? false,
          create: editModule.create ?? false,
          edit: editModule.edit ?? false,
          delete: editModule.delete ?? false,
        };

        // Preserve module-level special flags (e.g. hiring.viewOwnOnly).
        // Without this, the flag is silently dropped on save.
        Object.keys(editModule).forEach(key => {
          if (!['view', 'create', 'edit', 'delete', 'subPages'].includes(key)) {
            completePermissions[module][key] = editModule[key];
          }
        });

        if (defaultPermissions[module]?.subPages) {
          completePermissions[module].subPages = {};
          Object.keys(defaultPermissions[module].subPages).forEach(subPage => {
            const editSubPage = editModule.subPages?.[subPage] || {};
            completePermissions[module].subPages[subPage] = {};
            Object.keys(defaultPermissions[module].subPages[subPage]).forEach(action => {
              completePermissions[module].subPages[subPage][action] = editSubPage[action] ?? false;
            });
          });
        }
      });
      
      console.log('=== SAVING PERMISSIONS ===');
      console.log('User ID:', editingUserId);
      console.log('Complete permissions to save:', JSON.stringify(completePermissions, null, 2));
      
      const response = await axios.put(`${API}/users/${editingUserId}/permissions`, 
        { permissions: completePermissions },
        { headers: getAuthHeader() }
      );
      console.log('Save response:', response.data);
      toast.success('Permissions updated successfully');
      setEditPermissionsDialogOpen(false);
      setEditingUserId(null);
      setEditingPermissions({});
      fetchUsers();
    } catch (error) {
      console.error('Update permissions error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update permissions';
      toast.error(errorMessage);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Super Admin': 'bg-purple-900 text-white',
      'Admin': 'bg-red-100 text-red-800',
      'Checker': 'bg-orange-100 text-orange-800',
      'Fleet Manager': 'bg-blue-100 text-blue-800',
      'Repair Technician': 'bg-green-100 text-green-800',
      'Accounts Team': 'bg-purple-100 text-purple-800',
      'Management': 'bg-yellow-100 text-yellow-800',
      'Insurance Manager': 'bg-cyan-100 text-cyan-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  // Check if current user is admin or super admin
  const isAdmin = currentUser?.role === 'Admin' || currentUser?.role === 'Super Admin';
  const isSuperAdmin = currentUser?.role === 'Super Admin';

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
            <p className="text-slate-600">Only administrators can manage users.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">{t('users.title')}</h1>
          <p className="text-slate-600 mt-1">{t('users.subtitle')}</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-lime-600 to-green-600" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('users.addUser')}
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('users.addUser')}</DialogTitle>
              <DialogDescription>{t('users.subtitle')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="user-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                  data-testid="user-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="user-password"
                />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value, customRole: value === 'Custom' ? formData.customRole : '' })}
                  required
                >
                  <SelectTrigger data-testid="user-role">
                    <SelectValue placeholder="Select user role" />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperAdmin && <SelectItem value="Super Admin">Super Admin (Full Control)</SelectItem>}
                    <SelectItem value="Admin">Admin (Full Access)</SelectItem>
                    <SelectItem value="Fleet Manager">Fleet Manager</SelectItem>
                    <SelectItem value="Repair Technician">Repair Technician</SelectItem>
                    <SelectItem value="Accounts Team">Accounts Team</SelectItem>
                    <SelectItem value="Management">Management</SelectItem>
                    <SelectItem value="Insurance Manager">Insurance Manager</SelectItem>
                    <SelectItem value="Checker">Checker</SelectItem>
                    <SelectItem value="Custom">+ Add Custom Role</SelectItem>
                  </SelectContent>
                </Select>
                {formData.role === 'Custom' && (
                  <Input
                    placeholder="Enter custom role name"
                    value={formData.customRole}
                    onChange={(e) => setFormData({ ...formData, customRole: e.target.value })}
                    className="mt-2"
                    data-testid="custom-role-input"
                    required
                  />
                )}
              </div>
              
              {/* Permissions Button */}
              <div className="space-y-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setPermissionsDialogOpen(true)}
                  className="w-full border-2 border-lime-500 text-lime-700 hover:bg-lime-50"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Manage Custom Permissions
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Click to define specific access and editing permissions for this user
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>Tip:</strong> Use custom permissions to control exactly what this user can view, create, edit, or delete.
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="modern-btn-primary bg-gradient-to-r from-lime-600 to-green-600" data-testid="submit-user-btn">
                  Add User
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>System Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">
                        {user.name}
                        {user.id === currentUser.id && (
                          <Badge className="ml-2 bg-yellow-100 text-yellow-800">You</Badge>
                        )}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatDateDisplay(user.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {/* Edit Permissions button - available for non-Super Admin users, and cannot edit own permissions */}
                          {user.role !== 'Super Admin' && user.role !== 'Admin' && isAdmin && user.id !== currentUser.id && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="gap-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                              onClick={() => handleOpenEditPermissions(user)}
                              data-testid={`edit-permissions-${user.id}`}
                              title="Edit Permissions"
                            >
                              <Shield className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Delete button */}
                          {user.id !== currentUser.id && (
                            <>
                              {user.role === 'Super Admin' ? (
                                <span className="text-xs text-slate-400" title="Super Admin cannot be deleted">Protected</span>
                              ) : user.role === 'Admin' && !isSuperAdmin ? (
                                <span className="text-xs text-slate-400" title="Only Super Admin can delete Admin users">Restricted</span>
                              ) : isAdmin && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                  onClick={() => handleDelete(user.id)}
                                  data-testid={`delete-user-${user.id}`}
                                  title="Delete User"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="modern-card border-0 bg-gradient-to-r from-blue-50 to-cyan-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Permission System Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 mb-3">
            <strong>New Feature:</strong> You can now assign granular permissions to each user. Click the shield icon on a user to edit their permissions.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-800">Available Modules:</h3>
              <ul className="space-y-1 text-xs text-slate-700">
                <li>• Dashboard, Motorcycles, Batteries</li>
                <li>• Riders, Lessees, Maintenance</li>
                <li>• Fines, Salik, Spares</li>
                <li>• Battery Swaps, Insurance, Reports</li>
                <li>• Approvals (Checker Permission), Users</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-800">Permission Types:</h3>
              <ul className="space-y-1 text-xs text-slate-700">
                <li><strong>View:</strong> Can see the module</li>
                <li><strong>Create:</strong> Can add new records</li>
                <li><strong>Edit:</strong> Can modify existing records</li>
                <li><strong>Delete:</strong> Can remove records</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Permissions Management Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Manage User Permissions
            </DialogTitle>
            <DialogDescription>
              Define specific access and editing permissions for {formData.name || 'this user'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-lime-50 border border-lime-200 rounded-lg p-4">
              <p className="text-sm text-lime-800">
                <strong>Tip:</strong> Check the boxes to grant permissions. View = Access to module, Create = Can add new, Edit = Can modify, Delete = Can remove.
              </p>
            </div>

            <div className="space-y-4">
              {Object.keys(defaultPermissions).map(module => {
                const permissions = formData.permissions[module] || defaultPermissions[module];
                const moduleNames = {
                  dashboard: 'Dashboard',
                  motorcycles: 'Motorcycles',
                  batteries: 'Batteries',
                  riders: 'Riders',
                  lessees: 'Lessees',
                  maintenance: 'Maintenance',
                  fines: 'Fines',
                  inbox: 'Inbox',
                  salik: 'Salik',
                  talent: 'Talent (PMS)',
                  tap: 'TAP',
                  hiring: 'Hiring',
                  spares: 'Spares',
                  batterySwaps: 'Battery Swaps',
                  insurance: 'Insurance',
                  invoices: 'Accounts',
                  inventory: 'Inventory',
                  reports: 'Reports',
                  analysis: 'Analysis (Battery Swaps)',
                  search: 'Search',
                  approvals: 'Approvals (Checker)',
                  users: 'Users Management',
                  adminTools: 'Admin Tools'
                };
                const hasSubPages = defaultPermissions[module]?.subPages && Object.keys(defaultPermissions[module].subPages).length > 0;

                return (
                  <Card key={module} className="border-2">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-semibold">{moduleNames[module]}</CardTitle>
                        <div className="flex gap-2">
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="outline"
                            onClick={() => setAllPermissions(module, true)}
                            className="text-xs"
                          >
                            Enable All
                          </Button>
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="outline"
                            onClick={() => setAllPermissions(module, false)}
                            className="text-xs"
                          >
                            Disable All
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Main module permissions */}
                      <div className="grid grid-cols-4 gap-4">
                        {['view', 'create', 'edit', 'delete'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={permissions[action]}
                              onChange={() => handlePermissionToggle(module, action)}
                              className="w-4 h-4 text-lime-600 border-gray-300 rounded focus:ring-lime-500"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                      
                      {/* Special permission for Hiring - View Only Own Candidates */}
                      {module === 'hiring' && (
                        <div className="border-t pt-3 mt-3">
                          <label className="flex items-center gap-2 cursor-pointer bg-amber-50 p-2 rounded border border-amber-200">
                            <input
                              type="checkbox"
                              checked={permissions.viewOwnOnly === true}
                              onChange={() => handlePermissionToggle(module, 'viewOwnOnly')}
                              className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-amber-800">View Only Own Candidates</span>
                              <p className="text-xs text-amber-600">User can only see candidates they enrolled</p>
                            </div>
                          </label>
                        </div>
                      )}
                      
                      {/* Sub-pages */}
                      {hasSubPages && (
                        <div className="border-t pt-3 mt-3">
                          <p className="text-xs font-semibold text-slate-500 mb-2">Sub-Pages:</p>
                          <div className="space-y-2 pl-4">
                            {Object.keys(defaultPermissions[module].subPages).map(subPage => {
                              const subPagePerms = permissions.subPages?.[subPage] || defaultPermissions[module].subPages[subPage];
                              const availableActions = Object.keys(defaultPermissions[module].subPages[subPage]);
                              
                              return (
                                <div key={subPage} className="bg-slate-50 rounded p-2">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-slate-700">{subPageNames[subPage] || subPage}</span>
                                    <div className="flex gap-1">
                                      <button
                                        type="button"
                                        onClick={() => setAllPermissions(module, true, subPage)}
                                        className="text-xs text-blue-600 hover:underline"
                                      >
                                        All
                                      </button>
                                      <span className="text-slate-300">|</span>
                                      <button
                                        type="button"
                                        onClick={() => setAllPermissions(module, false, subPage)}
                                        className="text-xs text-red-600 hover:underline"
                                      >
                                        None
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-3">
                                    {availableActions.map(action => (
                                      <label key={action} className="flex items-center gap-1 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={subPagePerms[action]}
                                          onChange={() => handlePermissionToggle(module, action, subPage)}
                                          className="w-3 h-3 text-lime-600 border-gray-300 rounded focus:ring-lime-500"
                                        />
                                        <span className="text-xs capitalize">{action}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setPermissionsDialogOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog open={editPermissionsDialogOpen} onOpenChange={setEditPermissionsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Edit Permissions for {editingUserName}
            </DialogTitle>
            <DialogDescription>
              Update access and editing permissions for this user
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Check the boxes to grant permissions. View = Access to module, Create = Can add new, Edit = Can modify, Delete = Can remove.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {Object.keys(defaultPermissions).map(module => {
                const moduleNames = {
                  dashboard: 'Dashboard',
                  motorcycles: 'Motorcycles',
                  batteries: 'Batteries',
                  riders: 'Riders',
                  lessees: 'Lessees',
                  maintenance: 'Maintenance',
                  fines: 'Fines',
                  inbox: 'Inbox',
                  salik: 'Salik',
                  talent: 'Talent (PMS)',
                  tap: 'TAP',
                  hiring: 'Hiring',
                  spares: 'Spares',
                  batterySwaps: 'Battery Swaps',
                  insurance: 'Insurance',
                  invoices: 'Accounts',
                  inventory: 'Inventory',
                  reports: 'Reports',
                  analysis: 'Analysis (Battery Swaps)',
                  search: 'Search',
                  approvals: 'Approvals (Checker)',
                  users: 'User Management',
                  adminTools: 'Admin Tools'
                };
                const permissions = editingPermissions[module] || defaultPermissions[module];
                const hasSubPages = defaultPermissions[module]?.subPages && Object.keys(defaultPermissions[module].subPages).length > 0;

                return (
                  <Card key={module} className="border-2">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-semibold">{moduleNames[module]}</CardTitle>
                        <div className="flex gap-2">
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="outline"
                            onClick={() => setAllEditPermissions(module, true)}
                            className="text-xs"
                          >
                            Enable All
                          </Button>
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="outline"
                            onClick={() => setAllEditPermissions(module, false)}
                            className="text-xs"
                          >
                            Disable All
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Main module permissions */}
                      <div className="grid grid-cols-4 gap-4">
                        {['view', 'create', 'edit', 'delete'].map(action => (
                          <label key={action} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={permissions[action]}
                              onChange={() => handleEditPermissionToggle(module, action)}
                              className="w-4 h-4 text-lime-600 border-gray-300 rounded focus:ring-lime-500"
                            />
                            <span className="text-sm capitalize">{action}</span>
                          </label>
                        ))}
                      </div>

                      {/* Special permission for Hiring - View Only Own Candidates */}
                      {module === 'hiring' && (
                        <div className="border-t pt-3 mt-3">
                          <label className="flex items-center gap-2 cursor-pointer bg-amber-50 p-2 rounded border border-amber-200">
                            <input
                              type="checkbox"
                              checked={permissions.viewOwnOnly === true}
                              onChange={() => handleEditPermissionToggle(module, 'viewOwnOnly')}
                              className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                              data-testid="edit-perm-hiring-viewownly"
                            />
                            <div>
                              <span className="text-sm font-medium text-amber-800">View Only Own Candidates</span>
                              <p className="text-xs text-amber-600">User can only see candidates they enrolled</p>
                            </div>
                          </label>
                        </div>
                      )}
                      
                      {/* Sub-pages */}
                      {hasSubPages && (
                        <div className="border-t pt-3 mt-3">
                          <p className="text-xs font-semibold text-slate-500 mb-2">Sub-Pages:</p>
                          <div className="space-y-2 pl-2">
                            {Object.keys(defaultPermissions[module].subPages).map(subPage => {
                              // Use permissions from merged state (which properly includes all subPages)
                              const subPagePerms = permissions.subPages?.[subPage] || {};
                              const availableActions = Object.keys(defaultPermissions[module].subPages[subPage]);
                              
                              return (
                                <div key={subPage} className="bg-slate-50 rounded p-2">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-slate-700">{subPageNames[subPage] || subPage}</span>
                                    <div className="flex gap-1">
                                      <button
                                        type="button"
                                        onClick={() => setAllEditPermissions(module, true, subPage)}
                                        className="text-xs text-blue-600 hover:underline"
                                      >
                                        All
                                      </button>
                                      <span className="text-slate-300">|</span>
                                      <button
                                        type="button"
                                        onClick={() => setAllEditPermissions(module, false, subPage)}
                                        className="text-xs text-red-600 hover:underline"
                                      >
                                        None
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-3">
                                    {availableActions.map(action => (
                                      <label key={action} className="flex items-center gap-1 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={subPagePerms[action] === true}
                                          data-testid={`edit-perm-${module}-${subPage}-${action}`}
                                          onChange={() => handleEditPermissionToggle(module, action, subPage)}
                                          className="w-3 h-3 text-lime-600 border-gray-300 rounded focus:ring-lime-500"
                                        />
                                        <span className="text-xs capitalize">{action}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setEditPermissionsDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={handleSavePermissions}
                className="bg-gradient-to-r from-lime-600 to-green-600"
              >
                Save Permissions
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
